import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Ticket, Monitor, BarChart3, Users, Settings, LogOut, CheckCircle, Home, ClipboardList, Wifi, UserPlus, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/contexts/AuthContext";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_helpdesk-hub-53/artifacts/dtuif3s8_NEWGamerLogo.jpg";

export default function AppLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isStaff, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  const navItems = [
    { path: '/', label: 'Dashboard', icon: Home, staffOnly: false },
    { path: '/tickets/managed', label: 'Tickets', icon: Ticket, staffOnly: true },
    { path: '/tickets/approval', label: 'Approvals', icon: CheckCircle, staffOnly: true },
    { path: '/assets', label: 'Assets', icon: Monitor, staffOnly: true },
    { path: '/network', label: 'Network', icon: Wifi, staffOnly: true },
    { path: '/onboarding', label: 'Onboarding', icon: UserPlus, staffOnly: true },
    { path: '/kb', label: 'KB', icon: BookOpen, staffOnly: true },
    { path: '/users', label: 'Users', icon: Users, staffOnly: true },
    { path: '/analytics', label: 'Analytics', icon: BarChart3, staffOnly: true },
    { path: '/reports', label: 'Reports', icon: ClipboardList, staffOnly: true },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Fixed Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-4 py-2">
          {/* Logo */}
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <img
              src={LOGO_URL}
              alt="Gamer Packaging"
              className="h-8 object-contain"
              data-testid="company-logo"
            />
            <div className="hidden sm:block">
              <h1 className="font-heading text-sm font-bold tracking-wide text-slate-800">IT Help Desk</h1>
              <p className="text-[10px] text-slate-400 -mt-0.5">{user?.name || 'Administrator'}</p>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex items-center gap-1">
            {navItems.map((item) => {
              if (item.staffOnly && !isStaff) return null;
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Button
                  key={item.path}
                  variant={active ? "default" : "ghost"}
                  size="sm"
                  className={`text-xs px-2 py-1 h-8 ${active ? 'bg-slate-800 text-white' : 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'}`}
                  onClick={() => navigate(item.path)}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon className="h-3.5 w-3.5 mr-1" />
                  <span className="hidden md:inline">{item.label}</span>
                </Button>
              );
            })}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            {isStaff && <NotificationBell />}
            
            {isStaff && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs text-slate-500 hover:text-slate-700"
                onClick={() => navigate('/tickets/settings')}
                data-testid="settings-btn"
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-slate-500 hover:text-red-600"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-4 md:p-6">
        {children}
      </main>
    </div>
  );
}
